import React from "react";

import { Icon, IconSize } from "@gaming/xds-react-js";
import { Dislike, DislikeSolid, Like, LikeSolid, Warning } from "@gaming/xds-react-js-icons";

import {
  AssistantAnswer as AssistantAnswerData,
  AssistantLink as AssistantLinkData,
  supportAssistantStore,
} from "../../helpers/supportAssistantStore";
import { useStrings } from "../../localization/localizationHelpers";
import { CopyButton } from "./CopyButton";
import { EscalationCard } from "./EscalationCard";
import {
  AssistantLink,
  isAllowedAssistantLink,
  renderAssistantMarkdown,
} from "./renderAssistantMarkdown";
import * as styles from "./SupportAssistant.module.css";

type AssistantAnswerProps = Readonly<{ answer: AssistantAnswerData }>;

const renderLinks = (links: ReadonlyArray<AssistantLinkData>) => (
  <ul className={styles.linkList}>
    {links
      .filter(link => isAllowedAssistantLink(link.url))
      .map(link => (
        <li key={link.url}>
          <AssistantLink href={link.url} className={styles.inlineLink}>
            {link.title || link.url}
          </AssistantLink>
        </li>
      ))}
  </ul>
);

export const AssistantAnswer: React.FC<AssistantAnswerProps> = ({ answer }) => {
  const [
    recommendedDocs,
    sourcesConsulted,
    aiDisclaimer,
    copyAnswer,
    helpful,
    notHelpful,
    docsUnavailable,
    contextUnavailable,
  ] = useStrings(
    "supportAssistant_recommendedDocs",
    "supportAssistant_sourcesConsulted",
    "supportAssistant_aiDisclaimer",
    "supportAssistant_copyAnswer",
    "supportAssistant_helpful",
    "supportAssistant_notHelpful",
    "supportAssistant_warningDocsUnavailable",
    "supportAssistant_warningContextUnavailable"
  );

  return (
    <article className={styles.answer} data-testid="support-assistant-answer">
      {answer.warnings.map(warning => (
        <p key={warning} className={styles.notice}>
          <Icon aria-hidden="true" icon={Warning} iconSize={IconSize.Custom} />
          {warning === "DOCS_UNAVAILABLE" ? docsUnavailable : contextUnavailable}
        </p>
      ))}
      <div className={styles.markdown}>{renderAssistantMarkdown(answer.markdown)}</div>
      {answer.citations.length > 0 && (
        <div data-testid="support-assistant-citations">
          <h4 className={styles.subheading}>{recommendedDocs}</h4>
          {renderLinks(answer.citations)}
        </div>
      )}
      {answer.escalation && <EscalationCard escalation={answer.escalation} />}
      {answer.sourcesConsulted.length > 0 && (
        <details className={styles.details}>
          <summary>
            {sourcesConsulted.replace("{0}", String(answer.sourcesConsulted.length))}
          </summary>
          {renderLinks(answer.sourcesConsulted)}
        </details>
      )}
      <span className={styles.disclaimer}>{aiDisclaimer}</span>
      <div className={styles.actions}>
        <CopyButton label={copyAnswer} text={answer.markdown} />
        {(
          [
            ["helpful", helpful, Like, LikeSolid],
            ["notHelpful", notHelpful, Dislike, DislikeSolid],
          ] as const
        ).map(([rating, label, icon, activeIcon]) => (
          <button
            key={rating}
            type="button"
            className={styles.iconButton}
            aria-label={label}
            title={label}
            aria-pressed={answer.rating === rating}
            onClick={() => supportAssistantStore.rate(answer.correlationId, rating)}
          >
            <Icon
              aria-hidden="true"
              icon={answer.rating === rating ? activeIcon : icon}
              iconSize={IconSize.Custom}
            />
          </button>
        ))}
      </div>
    </article>
  );
};
